import { useState } from 'react';
import { AuthService } from '@evimo/client/openapi/services/AuthService';
import { useAuth } from '~/app/auth/context/AuthContext';
import { z } from 'zod';
import { message } from 'antd';
import { useFormButtonDisabled } from '@evimo/hooks/useFormButtonDisabled';
import { Profile } from './Profile';
import { useMutation } from '@tanstack/react-query';
import { Language } from '@evimo/types/enums/language';
import { useTranslation } from 'react-i18next';
import { UpdateMeRequestDto } from '@evimo/client/openapi/models/UpdateMeRequestDto';
import { ApiError } from '@evimo/client/openapi/core/ApiError';

const passwordSchema = z.object({
  currentPassword: z.string().min(6, 'Old password is required'),
  newPassword: z.string().min(6, 'New password must be at least 6 characters'),
});
const nameSchema = z.object({
  name: z.string().min(1, 'Name required'),
  email: z.email('Invalid email'),
});

export const ProfileContainer: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation(['common', 'userAccount']);
  const [name, setName] = useState(user?.name || '');
  const [language, setLanguage] = useState<Language>(
    user?.language || Language.EN,
  );

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [passwordError, setPasswordError] = useState<{
    currentPassword?: string;
    newPassword?: string;
  }>({});
  const [nameError, setNameError] = useState<{ name?: string; email?: string }>(
    {},
  );

  const { meControllerUpdateCurrentUser } = AuthService;

  const updateMeMutation = useMutation({
    mutationFn: meControllerUpdateCurrentUser,
    onError(error: ApiError) {
      message.error(error.message);
    },
    onSuccess() {
      message.success(t('common:myAccount.profile.successUpdate'));
    },
  });

  const languageOptions = [
    { label: t('common:languages.en'), value: Language.EN },
    { label: t('common:languages.fr'), value: Language.FR },
  ];

  const nameFormValues = { name, email: user?.email };
  const isNameFormDisabled =
    useFormButtonDisabled({
      values: nameFormValues,
      initialValues: { name: user?.name || '', email: user?.email || '' },
      requiredFields: ['name', 'email'],
    }) || !nameSchema.safeParse(nameFormValues).success;

  const [passwordValues, setPasswordValues] = useState({
    currentPassword: '',
    newPassword: '',
  });

  const initialPasswordValues = { currentPassword: '', newPassword: '' };
  const zodPasswordResult = passwordSchema.safeParse(passwordValues);
  const isPasswordFormDisabled =
    useFormButtonDisabled({
      values: passwordValues,
      initialValues: initialPasswordValues,
      requiredFields: ['currentPassword', 'newPassword'],
    }) || !zodPasswordResult.success;

  const handleNameAndLanguageSave = () => {
    updateMeMutation.mutate({
      requestBody: {
        name,
        language: language as UpdateMeRequestDto.language,
      },
    });
  };

  const handlePasswordFinish = async (
    currentPassword: string,
    newPassword: string,
  ) => {
    updateMeMutation.mutate({
      requestBody: {
        currentPassword,
        newPassword,
      },
    });
  };

  return (
    <Profile
      user={user}
      name={name}
      t={t}
      setName={setName}
      language={language}
      setLanguage={setLanguage}
      languageOptions={languageOptions}
      saving={updateMeMutation.isPending}
      showOld={showOld}
      setShowOld={setShowOld}
      showNew={showNew}
      setShowNew={setShowNew}
      passwordError={passwordError}
      setPasswordError={setPasswordError}
      nameError={nameError}
      setNameError={setNameError}
      isNameFormDisabled={isNameFormDisabled}
      handleNameSave={handleNameAndLanguageSave}
      passwordValues={passwordValues}
      setPasswordValues={setPasswordValues}
      isPasswordFormDisabled={isPasswordFormDisabled}
      handlePasswordFinish={handlePasswordFinish}
    />
  );
};
